import { useEffect, useRef } from 'react'
import * as THREE from 'three'

export const NetworkGraph = () => {
  const mountRef = useRef<HTMLDivElement>(null)
  const sceneRef = useRef<THREE.Scene | null>(null)
  const rendererRef = useRef<THREE.WebGLRenderer | null>(null)
  const frameRef = useRef<number>(0)

  useEffect(() => {
    if (!mountRef.current) return

    const width = mountRef.current.clientWidth
    const height = mountRef.current.clientHeight

    // Scene setup
    const scene = new THREE.Scene()
    sceneRef.current = scene

    // Camera setup - moved closer for larger appearance
    const camera = new THREE.PerspectiveCamera(75, width / height, 0.1, 1000)
    camera.position.z = 20

    // Renderer setup
    const renderer = new THREE.WebGLRenderer({ alpha: true, antialias: true })
    renderer.setSize(width, height)
    renderer.setPixelRatio(window.devicePixelRatio)
    rendererRef.current = renderer
    mountRef.current.appendChild(renderer.domElement)

    // Create nodes - smaller and using Titan White
    const nodeGeometry = new THREE.SphereGeometry(0.2, 32, 32) // Much smaller nodes
    const nodeMaterial = new THREE.MeshBasicMaterial({
      color: 0xebf0ff, // Titan White
      opacity: 0.2, // Even more transparent
      transparent: true,
    })

    const nodes: THREE.Mesh[] = []
    const nodePositions: THREE.Vector3[] = []
    const nodeCount = 20

    // Generate random positions for nodes
    for (let i = 0; i < nodeCount; i++) {
      const node = new THREE.Mesh(nodeGeometry, nodeMaterial.clone())
      const position = new THREE.Vector3(
        (Math.random() - 0.5) * 60, // Expanded area
        (Math.random() - 0.5) * 30,
        (Math.random() - 0.5) * 30,
      )
      node.position.copy(position)
      nodePositions.push(position)
      nodes.push(node)
      scene.add(node)
    }

    // Create connections
    const lineMaterial = new THREE.LineBasicMaterial({
      color: 0xebf0ff, // Titan White
      opacity: 0.15, // Very transparent
      transparent: true,
    })

    const connections: THREE.Line[] = []

    // Connect nodes randomly
    for (let i = 0; i < nodeCount; i++) {
      const connectionsCount = Math.floor(Math.random() * 3) + 1
      for (let j = 0; j < connectionsCount; j++) {
        const targetIndex = Math.floor(Math.random() * nodeCount)
        if (targetIndex !== i) {
          const geometry = new THREE.BufferGeometry()
          const positions = new Float32Array(6)
          positions[0] = nodePositions[i].x
          positions[1] = nodePositions[i].y
          positions[2] = nodePositions[i].z
          positions[3] = nodePositions[targetIndex].x
          positions[4] = nodePositions[targetIndex].y
          positions[5] = nodePositions[targetIndex].z
          geometry.setAttribute(
            'position',
            new THREE.BufferAttribute(positions, 3),
          )

          const line = new THREE.Line(geometry, lineMaterial)
          connections.push(line)
          scene.add(line)
        }
      }
    }

    // Add some larger accent nodes with glow effect
    const accentNodeGeometry = new THREE.SphereGeometry(0.3, 32, 32) // Much smaller accent nodes
    const accentNodeMaterial = new THREE.MeshBasicMaterial({
      color: 0xebf0ff, // Titan White
      opacity: 0.25, // More transparent
      transparent: true,
    })

    for (let i = 0; i < 3; i++) {
      const accentNode = new THREE.Mesh(accentNodeGeometry, accentNodeMaterial)
      accentNode.position.set(
        (Math.random() - 0.5) * 45, // Expanded area
        (Math.random() - 0.5) * 22,
        (Math.random() - 0.5) * 22,
      )
      nodes.push(accentNode)
      scene.add(accentNode)
    }

    // Animation
    const animate = () => {
      frameRef.current = requestAnimationFrame(animate)

      // Rotate the entire scene slowly
      scene.rotation.y += 0.0005
      scene.rotation.x += 0.0002

      // Pulse nodes
      nodes.forEach((node, index) => {
        const scale = 1 + Math.sin(Date.now() * 0.001 + index) * 0.1
        node.scale.set(scale, scale, scale)
      })

      // Update line positions for floating effect
      connections.forEach((line, index) => {
        const positions = line.geometry.attributes.position
          .array as Float32Array
        const offset = Math.sin(Date.now() * 0.0005 + index) * 0.1
        positions[1] += offset * 0.01
        positions[4] += offset * 0.01
        line.geometry.attributes.position.needsUpdate = true
      })

      renderer.render(scene, camera)
    }

    // Handle resize
    const handleResize = () => {
      if (!mountRef.current) return
      const width = mountRef.current.clientWidth
      const height = mountRef.current.clientHeight
      camera.aspect = width / height
      camera.updateProjectionMatrix()
      renderer.setSize(width, height)
    }

    window.addEventListener('resize', handleResize)
    animate()

    // Cleanup
    return () => {
      window.removeEventListener('resize', handleResize)
      if (frameRef.current) {
        cancelAnimationFrame(frameRef.current)
      }
      if (mountRef.current && renderer.domElement) {
        mountRef.current.removeChild(renderer.domElement)
      }
      renderer.dispose()
    }
  }, [])

  return (
    <div
      ref={mountRef}
      className='absolute inset-0 opacity-30' // Reduced overall opacity
      style={{ pointerEvents: 'none' }}
    />
  )
}
